import {useState} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {useDownloadReporte, useStartReporte, type BateriaEvaluacion} from '@/hooks/bateria/use-bateria'
import {motivoDelError, translateApiError} from '@/lib/errors'

/** The detail query polls while the report runs, so the badge advances without a reload. */
export function ReporteControl({
  businessId,
  evaluacionId,
  evaluacion,
}: {
  businessId: string
  evaluacionId: string
  evaluacion: BateriaEvaluacion
}) {
  const {t} = useTranslation()
  const start = useStartReporte(businessId, evaluacionId)
  const download = useDownloadReporte(businessId, evaluacionId)
  const [error, setError] = useState<string | null>(null)
  const status = evaluacion.reporte_status
  const running = status === 'pendiente' || status === 'procesando'

  function generate() {
    setError(null)
    start.mutate(undefined, {
      onError: (err) => {
        // El guard de la ficha llega tipado: se dice a cuántas personas les falta, no el genérico.
        const detalle = motivoDelError(err)
        setError(
          detalle?.motivo === 'ficha_incompleta'
            ? t('admin.bateria.reporte.fichaIncompleta', {count: Number(detalle.n_personas) || 1})
            : translateApiError(err)
        )
      },
    })
  }

  function downloadReporte() {
    setError(null)
    download.mutate(undefined, {onError: (err) => setError(translateApiError(err))})
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Siempre alcanzable, también con la evaluación cerrada: ahí se hace la revisión final. */}
      <Button asChild size="sm" variant="outline" data-testid="ir-al-informe">
        <Link to={`/admin/bateria/empresas/${businessId}/evaluaciones/${evaluacionId}/informe`}>
          {t('admin.bateria.reporte.verInforme')}
        </Link>
      </Button>
      <Button size="sm" data-testid="generar-reporte" disabled={running || start.isPending} onClick={generate}>
        {status === 'listo' ? t('admin.bateria.reporte.regenerate') : t('admin.bateria.reporte.generate')}
      </Button>
      {status === 'listo' && (
        <p className="w-full text-xs text-muted-foreground" data-testid="reporte-regenerate-hint">
          {t('admin.bateria.reporte.regenerateHint')}
        </p>
      )}
      {running && (
        <Tag tone="amber" data-testid="reporte-status">
          {t(`admin.bateria.reporte.status.${status}`)}
        </Tag>
      )}
      {status === 'listo' && (
        <>
          <Tag tone="green" data-testid="reporte-status">
            {t('admin.bateria.reporte.status.listo')}
          </Tag>
          <Button
            size="sm"
            variant="outline"
            data-testid="descargar-reporte"
            disabled={download.isPending}
            onClick={downloadReporte}>
            {t('admin.bateria.reporte.download')}
          </Button>
        </>
      )}
      {status === 'error' && (
        <Tag tone="red" data-testid="reporte-status">
          {t('admin.bateria.reporte.status.error')}
        </Tag>
      )}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  )
}
